import styled from '@emotion/native';
import { GroupSecurity } from '@quromedical/fhir-common';
import { PathologyProvider } from '@quromedical/models';
import { Col } from 'components/base';
import type { SubmissionHandler } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  Card,
  type Fetcher,
  Skeleton,
  SkeletonProvider,
  ToggleButton,
  ToggleCard,
} from 'design-system';
import type { UsePathologyActions } from 'hooks/usePathology';
import { useRevalidation } from 'hooks/useRevalidation';
import { PathologyApi } from 'integration/aggregate/';
import type React from 'react';
import { useCallback, useState } from 'react';
import { strings } from 'strings';

import {
  AmpathCreateOrderForm,
  createFields as createAmpathFields,
  type FormData,
  type TestType,
} from './ampath';
import {
  createFields as createLancetFields,
  LancetForm,
  type PathologyCreateOrderForm,
} from './lancet';

const pathologyApi = new PathologyApi();

type SupportedPathologyProviders = 'Lancet' | 'Ampath';

type SupportedPathologyProvidersRecord = Record<
  string,
  SupportedPathologyProviders
>;

const pathologyProviders: SupportedPathologyProvidersRecord = {
  lancet: 'Lancet',
  ampath: 'Ampath',
};

interface Props {
  patientId: string;
  practitionerFetcher: Fetcher<string>;
  usePathologyProps: UsePathologyActions;
}

const ToggleWrapper = styled(Col)(({ theme }) => ({
  backgroundColor: theme.color['base-grey'],
  justifyContent: 'center',
  alignItems: 'center',
}));

const createFields = (
  testType: TestType,
  practitionerFetcher: Fetcher<string>,
) => {
  const ampathFields = createAmpathFields(testType, practitionerFetcher);
  const lancetFields = createLancetFields();

  return { ampathFields, lancetFields };
};

export const ServiceRequestForm: React.FC<Props> = ({
  patientId,
  practitionerFetcher,
  usePathologyProps,
}) => {
  const revalidate = useRevalidation('ServiceRequest');
  const [testType, setTestType] = useState<TestType>('normal');
  const [toggleKey, setToggleKey] = useState(Date.now());

  const [toggleForm, setToggleForm] =
    useState<SupportedPathologyProviders>('Ampath');

  const { create, isMutationLoading } = usePathologyProps;

  const onSubmit = useCallback<SubmissionHandler<FormData>>(
    async (formData: FormData) => {
      try {
        await pathologyApi.createAmpathRequest({
          ...formData,
          patient: patientId,
        });
        setToggleKey(Date.now());
        revalidate();
        return {};
      } catch (error) {
        return { error };
      }
    },
    [revalidate, patientId],
  );

  const onLancetSubmit = useCallback<
    SubmissionHandler<PathologyCreateOrderForm>
  >(
    async (formData: PathologyCreateOrderForm) => {
      try {
        await create({
          ...formData,
          provider: PathologyProvider.lancet,
          patient: patientId,
        });
        setToggleKey(Date.now());
        revalidate();
        return {};
      } catch (error) {
        return { error };
      }
    },
    [patientId, create, revalidate],
  );

  const { ampathFields, lancetFields } = createFields(
    testType,
    practitionerFetcher,
  );

  const onChange = useCallback((formData: FormData) => {
    setTestType(formData.testType);
  }, []);

  const onToggleChange = useCallback((value: SupportedPathologyProviders) => {
    setToggleForm(value);
    return;
  }, []);

  return (
    <SkeletonProvider loading={isMutationLoading}>
      <Skeleton>
        <Card paddingHorizontal={0} paddingVertical={0}>
          <ToggleCard
            title={strings.PathologyCreateCardTitle}
            icon="biotech"
            blockColor="accent-green"
            textCollapse={strings.PathologyButtonTextHide}
            textExpand={strings.PathologyButtonTextCreateRequest}
            contentWrapperProps={{ paddingHorizontal: 0, paddingVertical: 0 }}
            key={toggleKey}
          >
            <ToggleWrapper>
              <Col alignItems="center" justifyContent="center">
                <WithGroup groups={[GroupSecurity.ServiceRequestLancet]}>
                  <ToggleButton<SupportedPathologyProviders>
                    value={toggleForm}
                    onChange={(value) => {
                      onToggleChange(value);
                    }}
                    options={[
                      {
                        value: pathologyProviders.ampath,
                        display: strings.PathologyProviderAmpath,
                      },
                      {
                        value: pathologyProviders.lancet,
                        display: strings.PathologyProviderLancet,
                      },
                    ]}
                  />
                </WithGroup>
              </Col>
            </ToggleWrapper>

            {toggleForm == 'Ampath' ? (
              <AmpathCreateOrderForm
                fields={ampathFields}
                onChange={onChange}
                onSubmit={onSubmit}
                testType={testType}
                key={testType}
              />
            ) : (
              <LancetForm
                fields={lancetFields}
                onChange={() => {
                  return;
                }}
                onSubmit={onLancetSubmit}
              />
            )}
          </ToggleCard>
        </Card>
      </Skeleton>
    </SkeletonProvider>
  );
};
